import api from "../api/axios";

export interface SessionTreatmentPlan {
  id: number;
  name: string;
}

export interface Session {
  id: number;
  patientId: number;
  patientUid: string;
  patientFirstName?: string;
  patientLastName?: string;
  treatmentPlanUid?: string;
  treatmentPlanStepId?: number;
  treatmentPlan?: SessionTreatmentPlan;
  accountId: number;
  providerFirstName?: string;
  providerLastName?: string;
  providerAvatarUrl?: string;
  type: number; // 1 = consult, 2 = treatment
  status: number; // 0 = draft, 1 = in progress, 2 = paused, 3 = completed, 4 = cancelled
  startedAt: string;
  finishedAt?: string;
  audioBlobPath?: string;
  transcript?: string;
  preliminaryDiagnosis?: string;
  notes?: string;
  duration?: string;
}

export interface GetSessionsParams {
  ids?: number[];
  patientId?: number[];
  patientUid?: string[];
  providerId?: number[]; // Filter by provider IDs
  treatmentPlanUids?: string[];
  types?: number[]; // 1 = Consult, 2 = Treatment
  statuses?: number[]; // 1 = Draft, 2 = InProgress, 3 = Paused, 4 = Completed, 5 = Canceled
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  start?: number; // pagination offset (default: 0)
  limit?: number; // page size (default: 20, max: 100)
  total?: boolean; // include total count in response (default: false)
  orderBy?: string; // sort field (default: StartedAt)
  order?: 0 | 1; // sort direction: 0 = Asc, 1 = Desc
}

export interface GetSessionsResponse {
  data: Session[];
  total?: number;
}

export interface CreateSessionRequest {
  patientId: number;
  treatmentPlanId?: number; // Required for treatment sessions with a plan; omit for planless
  treatmentPlanAppointmentId?: number; // Required for treatment sessions with a plan; omit for planless
  accountId: number;
  type: number; // 1 = consult, 2 = treatment
  notes?: string;
}

export interface CreateSessionResponse {
  id: number;
  patientId: number;
  treatmentPlanId?: number;
  treatmentPlanAppointmentId?: number;
  accountId: number;
  type: number;
  status: number;
  startedAt: string;
  finishedAt?: string;
  audioBlobPath?: string;
  transcript?: string;
  preliminaryDiagnosis?: string;
  notes?: string;
  duration?: string;
}

// Sessions Service - Real API implementation
export const sessionsService = {
  // GET /sessions - Get all sessions with filters and pagination
  getSessions: async (
    params?: GetSessionsParams,
  ): Promise<GetSessionsResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.ids?.length) {
      queryParams.append("ids", params.ids.join(","));
    }
    if (params?.patientId?.length) {
      params.patientId.forEach((id) => {
        queryParams.append("patientId", id.toString());
      });
    }
    if (params?.patientUid?.length) {
      params.patientUid.forEach((uid) => {
        queryParams.append("patientUid", uid);
      });
    }
    if (params?.providerId?.length) {
      params.providerId.forEach((id) => {
        queryParams.append("providerId", id.toString());
      });
    }
    if (params?.treatmentPlanUids?.length) {
      queryParams.append(
        "treatmentPlanUids",
        params.treatmentPlanUids.join(","),
      );
    }
    if (params?.types?.length) {
      queryParams.append("types", params.types.join(","));
    }
    if (params?.statuses?.length) {
      queryParams.append("statuses", params.statuses.join(","));
    }
    if (params?.search) {
      queryParams.append("search", params.search);
    }
    if (params?.dateFrom) {
      queryParams.append("dateFrom", params.dateFrom);
    }
    if (params?.dateTo) {
      queryParams.append("dateTo", params.dateTo);
    }
    if (params?.start !== undefined) {
      queryParams.append("start", params.start.toString());
    }
    if (params?.limit !== undefined) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params?.total !== undefined) {
      queryParams.append("total", params.total.toString());
    }
    if (params?.orderBy) {
      queryParams.append("order-by", params.orderBy);
    }
    if (params?.order !== undefined) {
      queryParams.append("order", params.order.toString());
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/sessions?${queryString}` : "/sessions";

    const response = await api.get<Session[]>(url);

    // Extract total from headers if available
    const total = response.headers["x-total-count"]
      ? parseInt(response.headers["x-total-count"], 10)
      : undefined;

    return {
      data: response.data,
      total,
    };
  },

  // GET /sessions/{id} - Get session by ID
  getSessionById: async (sessionId: number): Promise<Session> => {
    const response = await api.get<Session>(`/sessions/${sessionId}`);
    return response.data;
  },

  // Get sessions by patient
  getSessionsByPatient: async (patientUid: string): Promise<Session[]> => {
    const response = await api.get<Session[]>(
      `/sessions/by-patient/${patientUid}`,
    );
    return response.data;
  },

  // POST /sessions - Create new session
  createSession: async (
    data: CreateSessionRequest,
  ): Promise<CreateSessionResponse> => {
    const response = await api.post<CreateSessionResponse>("/sessions", data);
    return response.data;
  },

  // POST /sessions/{id}/start - Start session
  startSession: async (sessionId: number): Promise<void> => {
    await api.post(`/sessions/${sessionId}/start`);
  },

  // POST /sessions/{id}/pause - Pause session
  pauseSession: async (sessionId: number): Promise<void> => {
    await api.post(`/sessions/${sessionId}/pause`);
  },

  // POST /sessions/{id}/resume - Resume session
  resumeSession: async (sessionId: number): Promise<void> => {
    await api.post(`/sessions/${sessionId}/resume`);
  },

  // POST /sessions/{id}/cancel - Cancel session
  cancelSession: async (sessionId: number): Promise<void> => {
    await api.post(`/sessions/${sessionId}/cancel`);
  },

  // POST /sessions/{id}/audio/chunk?blockId={blockId} - Upload audio chunk
  uploadAudioChunk: async (
    sessionId: number,
    chunk: Blob,
    blockId: string,
  ): Promise<void> => {
    await api.post(
      `/sessions/${sessionId}/audio/chunk?blockId=${blockId}`,
      chunk,
      {
        headers: {
          "Content-Type": "application/octet-stream",
        },
      },
    );
  },

  // POST /sessions/{id}/audio/complete - Complete audio upload
  completeAudioUpload: async (
    sessionId: number,
    blockIds: string[],
  ): Promise<Blob> => {
    const response = await api.post(
      `/sessions/${sessionId}/audio/complete`,
      { blockIds },
      { responseType: "blob" },
    );
    return response.data;
  },

  // POST /sessions/{id}/save - Save session with transcript, notes and diagnosis
  saveSession: async (
    sessionId: number,
    data: {
      transcript?: string;
      preliminaryDiagnosis?: string;
      notes?: string;
      status?: number;
    },
  ): Promise<void> => {
    await api.post(`/sessions/${sessionId}/save`, data);
  },
};
